var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// components/button/common/src/button/button-props.ts
var defaultButtonProps = {
  type: "button",
  actionType: "secondary",
  status: "none",
  emphasis: "bold",
  size: "md",
  shape: "square",
  disabled: false,
  autoFocus: false,
  name: "",
  value: "",
  active: false
};
var buttonActionTypes = [
  "primary",
  "secondary",
  "destructive",
  "info",
  "contrast"
];
var buttonStatuses = ["none", "loading", "success", "error"];
var buttonEmphasises = ["bold", "minimal", "subtle"];
var buttonSizes = ["sm", "md", "lg", "xl"];
var buttonShapes = ["square", "circle"];
var buttonTypes = ["submit", "reset", "button"];

// components/button/common/src/button/button-style-sheet.ts
import { math } from "polished";
import {
  StyleSheet,
  browserSupportsSelector
} from "@gs-ux-uitoolkit-common/style";
import { getIconStyles, getPulseStyles } from "@gs-ux-uitoolkit-common/icon-font";
import {
  darkTheme,
  lightTheme,
  createComponentClassDefinitions
} from "@gs-ux-uitoolkit-common/theme";

// components/button/common/src/common/common-style-sheet.ts
var focusedBorderMargin = "-2px";
function applyButtonCommonBase() {
  return {
    display: "inline-block",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    // fix header bug
    position: "relative",
    // to position the ::before focus element
    boxSizing: "border-box",
    width: "100%"
  };
}
function applyButtonTypography(props) {
  const { size, theme } = props;
  if (size === "sm") {
    return theme.typography.heading07;
  }
  if (size === "md") {
    return theme.typography.heading06;
  }
  return theme.typography.heading05;
}
function applyButtonIconAndTextPositions(props) {
  const { emphasis, size } = props;
  const subtractBorders = emphasis === "subtle" ? "1px" : "0px";
  return {
    "> :only-child:not([data-gs-uitk-component='icon'])": {
      paddingLeft: `calc(${buttonSizeVariants[size].textOnlyPadding} - ${subtractBorders})`,
      paddingRight: `calc(${buttonSizeVariants[size].textOnlyPadding} - ${subtractBorders})`
    },
    "> [data-gs-uitk-component='icon']:only-child": {
      paddingLeft: `calc(${buttonSizeVariants[size].iconOnlyPadding} - ${subtractBorders})`,
      paddingRight: `calc(${buttonSizeVariants[size].iconOnlyPadding} - ${subtractBorders})`
    },
    "> [data-gs-uitk-component='icon']:first-child:not(:only-child)": {
      paddingLeft: buttonSizeVariants[size].iconOuterSidePadding,
      paddingRight: buttonSizeVariants[size].iconInnerSidePadding
    },
    "> :last-child:not(:only-child):not([data-gs-uitk-component='icon'])": {
      paddingRight: buttonSizeVariants[size].textOnlyPadding
    },
    "> :not([data-gs-uitk-component='icon']):first-child:not(:only-child)": {
      paddingLeft: buttonSizeVariants[size].textOnlyPadding
    },
    "> [data-gs-uitk-component='icon']:last-child:not(:only-child)": {
      paddingLeft: buttonSizeVariants[size].iconInnerSidePadding,
      paddingRight: buttonSizeVariants[size].iconOuterSidePadding
    }
  };
}
var buttonSizeVariants = {
  sm: {
    buttonHeight: "24px",
    buttonPaddingY: "4px",
    iconFontSize: "16px",
    textOnlyPadding: "12px",
    iconOnlyPadding: "4px",
    iconOuterSidePadding: "6px",
    iconInnerSidePadding: "4px",
    circleRadius: "14px"
  },
  md: {
    buttonHeight: "32px",
    buttonPaddingY: "6px",
    iconFontSize: "20px",
    textOnlyPadding: "16px",
    iconOnlyPadding: "6px",
    iconOuterSidePadding: "8px",
    iconInnerSidePadding: "6px",
    circleRadius: "18px"
  },
  lg: {
    buttonHeight: "40px",
    buttonPaddingY: "8px",
    iconFontSize: "24px",
    textOnlyPadding: "20px",
    iconOnlyPadding: "8px",
    iconOuterSidePadding: "10px",
    iconInnerSidePadding: "8px",
    circleRadius: "22px"
  },
  xl: {
    buttonHeight: "48px",
    buttonPaddingY: "12px",
    iconFontSize: "24px",
    textOnlyPadding: "32px",
    iconOnlyPadding: "12px",
    iconOuterSidePadding: "24px",
    iconInnerSidePadding: "12px",
    circleRadius: "26px"
  }
};

// components/button/common/src/button/button-style-sheet.ts
import { colors as colors2 } from "@gs-ux-uitoolkit-common/design-system";
import { isBrowser } from "@gs-ux-uitoolkit-common/shared";

// components/button/common/src/button/button-theme.ts
import { colors } from "@gs-ux-uitoolkit-common/design-system";
import { mix, rgba } from "polished";
function getButtonTheme(theme) {
  const primaryBoldColors = theme.getColorInteractionShades("primary", "bold");
  const primaryMinimalColors = theme.getColorInteractionShades("primary", "minimal");
  const secondaryBoldColors = theme.getColorInteractionShades("secondary", "bold");
  secondaryBoldColors.text = theme.colorMode == "light" ? theme.text.primary : theme.text.secondary;
  const secondaryMinimalColors = theme.getColorInteractionShades("secondary", "minimal");
  secondaryMinimalColors.text = theme.colorMode == "light" ? theme.text.primary : theme.text.secondary;
  secondaryMinimalColors.border = theme.border.moderate;
  secondaryMinimalColors.active = rgba(
    theme.increaseContrast(theme.colorScheme.secondary, 2).toString(),
    theme.colorMode === "light" ? 0.4 : 0.2
  );
  secondaryMinimalColors.hover = rgba(
    theme.increaseContrast(theme.colorScheme.secondary, 2).toString(),
    theme.colorMode === "light" ? 0.2 : 0.1
  );
  const destructiveBoldColors = theme.getColorInteractionShades("error", "bold");
  const destructiveMinimalColors = theme.getColorInteractionShades("error", "minimal");
  const infoBoldColors = theme.getColorInteractionShades("info", "bold");
  const infoMinimalColors = theme.getColorInteractionShades("info", "minimal");
  return {
    action: {
      primary: {
        emphasis: {
          bold: {
            backgroundColor: primaryBoldColors.background,
            backgroundHoverColor: primaryBoldColors.hover,
            backgroundActiveColor: primaryBoldColors.active,
            textColor: primaryBoldColors.text
          },
          minimal: {
            backgroundColor: primaryMinimalColors.background,
            backgroundHoverColor: primaryMinimalColors.hover,
            backgroundActiveColor: primaryMinimalColors.active,
            textColor: primaryMinimalColors.text
          },
          subtle: {
            backgroundColor: primaryMinimalColors.background,
            backgroundHoverColor: primaryMinimalColors.hover,
            backgroundActiveColor: primaryMinimalColors.active,
            borderColor: primaryMinimalColors.text,
            textColor: primaryMinimalColors.text
          }
        }
      },
      secondary: {
        emphasis: {
          bold: {
            backgroundColor: secondaryBoldColors.background,
            backgroundHoverColor: secondaryBoldColors.hover,
            backgroundActiveColor: secondaryBoldColors.active,
            textColor: secondaryBoldColors.text
          },
          minimal: {
            backgroundColor: secondaryMinimalColors.background,
            backgroundHoverColor: secondaryMinimalColors.hover,
            backgroundActiveColor: secondaryMinimalColors.active,
            textColor: secondaryMinimalColors.text
          },
          subtle: {
            backgroundColor: secondaryMinimalColors.background,
            backgroundHoverColor: secondaryMinimalColors.hover,
            backgroundActiveColor: secondaryMinimalColors.active,
            borderColor: secondaryMinimalColors.border,
            textColor: secondaryMinimalColors.text
          }
        }
      },
      destructive: {
        emphasis: {
          bold: {
            backgroundColor: destructiveBoldColors.background,
            backgroundHoverColor: destructiveBoldColors.hover,
            backgroundActiveColor: destructiveBoldColors.active,
            textColor: destructiveBoldColors.text
          },
          minimal: {
            backgroundColor: destructiveMinimalColors.background,
            backgroundHoverColor: destructiveMinimalColors.hover,
            backgroundActiveColor: destructiveMinimalColors.active,
            textColor: destructiveMinimalColors.text
          },
          subtle: {
            backgroundColor: destructiveMinimalColors.background,
            backgroundHoverColor: destructiveMinimalColors.hover,
            backgroundActiveColor: destructiveMinimalColors.active,
            borderColor: destructiveMinimalColors.text,
            textColor: destructiveMinimalColors.text
          }
        }
      },
      info: {
        emphasis: {
          bold: {
            backgroundColor: infoBoldColors.background,
            backgroundHoverColor: infoBoldColors.hover,
            backgroundActiveColor: infoBoldColors.active,
            textColor: infoBoldColors.text
          },
          minimal: {
            backgroundColor: infoMinimalColors.background,
            backgroundHoverColor: infoMinimalColors.hover,
            backgroundActiveColor: infoMinimalColors.active,
            textColor: infoMinimalColors.text
          },
          subtle: {
            backgroundColor: infoMinimalColors.background,
            backgroundHoverColor: infoMinimalColors.hover,
            backgroundActiveColor: infoMinimalColors.active,
            borderColor: infoMinimalColors.text,
            textColor: infoMinimalColors.text
          }
        }
      },
      contrast: {
        emphasis: {
          bold: {
            backgroundColor: colors.white,
            backgroundHoverColor: mix(0.1, colors.gray100, colors.white),
            backgroundActiveColor: mix(0.2, colors.gray100, colors.white),
            textColor: colors.gray110
          },
          minimal: {
            backgroundColor: "transparent",
            backgroundHoverColor: rgba(colors.white, 0.1),
            backgroundActiveColor: rgba(colors.white, 0.2),
            textColor: colors.white
          },
          subtle: {
            backgroundColor: "transparent",
            backgroundHoverColor: rgba(colors.white, 0.1),
            backgroundActiveColor: rgba(colors.white, 0.2),
            borderColor: colors.white,
            textColor: colors.white
          }
        }
      }
    },
    disabled: {
      primary: {
        emphasis: {
          bold: {
            backgroundColor: rgba(primaryBoldColors.background.toString(), 0.3),
            textColor: rgba(primaryBoldColors.text.toString(), 0.8),
            borderColor: "transparent"
          },
          minimal: {
            backgroundColor: "transparent",
            textColor: rgba(primaryMinimalColors.text.toString(), 0.4),
            borderColor: "transparent"
          },
          subtle: {
            backgroundColor: "transparent",
            borderColor: rgba(primaryMinimalColors.text.toString(), 0.4),
            textColor: rgba(primaryMinimalColors.text.toString(), 0.4)
          }
        }
      },
      secondary: {
        emphasis: {
          bold: {
            backgroundColor: rgba(secondaryBoldColors.background.toString(), 0.4),
            textColor: rgba(secondaryBoldColors.text.toString(), 0.5),
            borderColor: "transparent"
          },
          minimal: {
            backgroundColor: "transparent",
            textColor: rgba(secondaryMinimalColors.text.toString(), 0.4),
            borderColor: "transparent"
          },
          subtle: {
            backgroundColor: "transparent",
            borderColor: rgba(secondaryMinimalColors.text.toString(), 0.4),
            textColor: rgba(secondaryMinimalColors.text.toString(), 0.5)
          }
        }
      },
      destructive: {
        emphasis: {
          bold: {
            backgroundColor: rgba(destructiveBoldColors.background.toString(), 0.3),
            textColor: rgba(destructiveBoldColors.text.toString(), 0.8),
            borderColor: "transparent"
          },
          minimal: {
            backgroundColor: "transparent",
            textColor: rgba(destructiveMinimalColors.text.toString(), 0.4),
            borderColor: "transparent"
          },
          subtle: {
            backgroundColor: "transparent",
            borderColor: rgba(destructiveMinimalColors.text.toString(), 0.4),
            textColor: rgba(destructiveMinimalColors.text.toString(), 0.4)
          }
        }
      },
      info: {
        emphasis: {
          bold: {
            backgroundColor: rgba(infoBoldColors.background.toString(), 0.3),
            textColor: rgba(infoBoldColors.text.toString(), 0.8),
            borderColor: "transparent"
          },
          minimal: {
            backgroundColor: "transparent",
            textColor: rgba(infoMinimalColors.text.toString(), 0.4),
            borderColor: "transparent"
          },
          subtle: {
            backgroundColor: "transparent",
            borderColor: rgba(infoMinimalColors.text.toString(), 0.4),
            textColor: rgba(infoMinimalColors.text.toString(), 0.4)
          }
        }
      },
      contrast: {
        emphasis: {
          bold: {
            backgroundColor: rgba(colors.white, 0.4),
            textColor: rgba(colors.blue100, 0.4),
            borderColor: "transparent"
          },
          minimal: {
            backgroundColor: "transparent",
            textColor: rgba(colors.white, 0.4),
            borderColor: "transparent"
          },
          subtle: {
            backgroundColor: "transparent",
            borderColor: rgba(colors.white, 0.4),
            textColor: rgba(colors.white, 0.4)
          }
        }
      }
    },
    focus: {
      primary: {
        backgroundColor: primaryBoldColors.background,
        textColor: primaryBoldColors.text,
        borderColor: theme.colorScheme.primary
      },
      secondary: {
        backgroundColor: secondaryBoldColors.background,
        textColor: secondaryBoldColors.text,
        borderColor: theme.colorScheme.primary
      },
      destructive: {
        backgroundColor: destructiveBoldColors.background,
        textColor: destructiveBoldColors.text,
        borderColor: theme.colorScheme.primary
      },
      info: {
        backgroundColor: infoBoldColors.background,
        textColor: infoBoldColors.text,
        borderColor: theme.colorScheme.primary
      },
      contrast: {
        backgroundColor: colors.white,
        textColor: colors.blue100,
        borderColor: colors.white
      }
    }
  };
}

// components/button/common/src/button/button-style-sheet.ts
var borderWidth = "1px";
var buttonStyleSheet = new StyleSheet("button", (props) => {
  var _a;
  return createComponentClassDefinitions(
    props,
    createDefaultStyledClasses,
    (_a = props.theme.styleOverrides) == null ? void 0 : _a.button
  );
});
function createDefaultStyledClasses({
  theme,
  actionType = defaultButtonProps.actionType,
  status = defaultButtonProps.status,
  emphasis = defaultButtonProps.emphasis,
  size = defaultButtonProps.size,
  shape = defaultButtonProps.shape,
  disabled = defaultButtonProps.disabled,
  active = defaultButtonProps.active
}) {
  return {
    root: {
      display: "inline-block"
    },
    button: __spreadValues(__spreadProps(__spreadValues({}, applyButtonBase({
      theme,
      actionType,
      emphasis,
      size,
      status,
      shape,
      disabled,
      active
    })), {
      "&:before": __spreadValues({}, applyFocusBorderBase({ size, shape })),
      "&:hover:not(:disabled)": __spreadValues({}, applyButtonHover({ theme, actionType, emphasis, status })),
      "&:disabled": {
        opacity: theme.state.disabledOpacity,
        cursor: "not-allowed"
      },
      "&:not(:disabled):active": __spreadProps(__spreadValues({}, applyButtonActive({ theme, actionType, emphasis, status })), {
        // Sept 19, 2022: We had to conditionally apply focus-visible due to its unavailability in
        //    Chrome versions 85 and below. ION Dealbook was using Chrome 80 in Marquee Desktop OpenFin
        //    where :focus-visible is not supported. See this ticket for context: https://jira.site.gs.com/browse/UX-15248
        [!isBrowser || browserSupportsSelector(":focus-visible") ? "&:focus-visible" : "&:focus"]: {
          "&:before": __spreadValues({}, applyFocusBorderWhenNotFocused({ size, shape }))
        }
      }),
      // Sept 19, 2022: We had to conditionally apply focus-visible due to its unavailability in
      //    Chrome versions 85 and below. ION Dealbook was using Chrome 80 in Marquee Desktop OpenFin
      //    where :focus-visible is not supported. See this ticket for context: https://jira.site.gs.com/browse/UX-15248
      [!isBrowser || browserSupportsSelector(":focus-visible") ? "&:focus-visible" : "&:focus"]: __spreadProps(__spreadValues({}, applyButtonFocus({ theme, actionType, emphasis, status })), {
        "&:before": __spreadValues({}, applyFocusBorderWhenFocused({
          theme,
          emphasis,
          size,
          shape,
          actionType
        }))
      }),
      "&:after": __spreadValues({}, applyButtonStatusIcon({ theme, actionType, emphasis, size, status })),
      '[data-gs-uitk-component="icon"]': __spreadValues({}, applyIconStyles({ actionType, size, status }))
    }), applyButtonIconAndTextPositions({ emphasis, size }))
  };
}
function applyButtonBase(props) {
  const { theme, actionType, emphasis, size, status, shape, disabled, active } = props;
  const buttonTheme = getButtonTheme(theme);
  const notSubtleStyles = {
    padding: `${buttonSizeVariants[size].buttonPaddingY} 0px`,
    border: "0px"
  };
  const subtleStyles = {
    padding: `calc(${buttonSizeVariants[size].buttonPaddingY} - ${borderWidth}) 0px`,
    transition: "border-color 0.15s ease-in-out",
    border: `${borderWidth} solid ${buttonTheme.action[actionType].emphasis.subtle.borderColor}`
  };
  const actionTypeStyles = emphasis == "subtle" ? subtleStyles : notSubtleStyles;
  const activeStyles = active && !disabled ? applyButtonActive({ theme, actionType, emphasis, status }) : {};
  return __spreadValues(__spreadValues(__spreadValues(__spreadProps(__spreadValues(__spreadProps(__spreadValues({}, applyButtonCommonBase()), {
    height: buttonSizeVariants[size].buttonHeight,
    borderRadius: shape === "circle" ? buttonSizeVariants[size].circleRadius : "2px"
  }), applyButtonTypography({ size, theme })), {
    background: buttonTheme.action[actionType].emphasis[emphasis].backgroundColor,
    color: buttonTheme.action[actionType].emphasis[emphasis].textColor
  }), actionTypeStyles), applyButtonBaseStatus({ theme, actionType, emphasis, status })), activeStyles);
}
function applyButtonBaseStatus(props) {
  const { theme, actionType, emphasis, status } = props;
  const commonStatusStyles = {
    color: "transparent"
  };
  if (status === "none") {
    return {};
  } else if (emphasis === "bold" && actionType === "primary") {
    return __spreadProps(__spreadValues({}, commonStatusStyles), {
      background: theme.status[status].bold
    });
  } else {
    return __spreadValues({}, commonStatusStyles);
  }
}
function applyButtonHover(props) {
  const { theme, actionType, emphasis, status } = props;
  const buttonTheme = getButtonTheme(theme);
  return __spreadValues({
    background: buttonTheme.action[actionType].emphasis[emphasis].backgroundHoverColor
  }, applyButtonHoverStatus({ theme, actionType, emphasis, status }));
}
function applyButtonHoverStatus(props) {
  const { theme, actionType, emphasis, status } = props;
  if (emphasis === "bold" && actionType === "primary" && status !== "none") {
    return {
      background: theme.status[status].bold
    };
  } else {
    return {};
  }
}
function applyButtonActive(props) {
  const { theme, actionType, emphasis, status } = props;
  const buttonTheme = getButtonTheme(theme);
  const text = theme.increaseContrast(
    buttonTheme.action[actionType].emphasis[emphasis].textColor,
    1
  );
  const background = buttonTheme.action[actionType].emphasis[emphasis].backgroundActiveColor;
  return __spreadValues({
    color: status !== "none" ? "transparent" : text,
    background
  }, applyButtonActiveStatus({ theme, actionType, emphasis, status }));
}
function applyButtonActiveStatus(props) {
  const { theme, actionType, emphasis, status } = props;
  const interactionShades = theme.getColorInteractionShades(status, emphasis);
  if (emphasis === "bold" && actionType === "primary" && status !== "none") {
    return {
      background: interactionShades.active
    };
  } else {
    return {};
  }
}
function applyButtonStatusIcon(props) {
  const { theme, actionType, emphasis, size, status } = props;
  const buttonTheme = getButtonTheme(theme);
  const iconFontSize = buttonSizeVariants[size].iconFontSize;
  const commonStyles = {
    position: "absolute",
    left: `calc(50% - ${iconFontSize} / 2)`,
    fontSize: iconFontSize
  };
  const actionTextColor = buttonTheme.action[actionType].emphasis[emphasis].textColor;
  const keepActionTextColor = emphasis === "bold" && (actionType === "primary" || actionType === "destructive");
  const isDarkTheme = theme === darkTheme;
  const isLightTheme = theme === lightTheme;
  switch (status) {
    case "loading":
      return __spreadValues(__spreadProps(__spreadValues(__spreadValues({}, getIconStyles({ name: "loading", type: "filled" })), commonStyles), {
        // edge case for info and contrast actionType
        color: keepActionTextColor ? actionTextColor : actionType === "info" && isLightTheme ? colors2.blue050 : actionType === "contrast" || actionType === "info" && isDarkTheme ? colors2.blue060 : theme.status[status].bold
      }), getPulseStyles());
    case "success":
      return __spreadProps(__spreadValues(__spreadValues({}, getIconStyles({ name: "check-circle", type: "outlined" })), commonStyles), {
        // edge case for info and contrast actionType
        color: keepActionTextColor ? actionTextColor : actionType === "info" && isLightTheme ? colors2.green050 : actionType === "contrast" || actionType === "info" && isDarkTheme ? colors2.green060 : theme.status[status].bold
      });
    case "error":
      return __spreadProps(__spreadValues(__spreadValues({}, getIconStyles({ name: "error-outline", type: "outlined" })), commonStyles), {
        // edge case for info and contrast actionType
        color: keepActionTextColor ? actionTextColor : actionType === "info" && isLightTheme ? colors2.red050 : actionType === "contrast" || actionType === "info" && isDarkTheme ? colors2.red060 : theme.status[status].bold
        // edge case for info actionType, since the default status base color does not pop up enough on dark background
      });
    case "none":
    default:
      return {};
  }
}
function applyFocusBorderBase(props) {
  const { size, shape } = props;
  return __spreadProps(__spreadValues({}, applyFocusBorderWhenNotFocused({ size, shape })), {
    content: '""',
    position: "absolute",
    display: "block",
    transition: "border-color 0.15s ease-in-out, border-radius 0.15s ease-in-out, top 0.15s ease-in-out, right 0.15s ease-in-out, bottom 0.15s ease-in-out, left 0.15s ease-in-out"
  });
}
function applyFocusBorderWhenFocused(props) {
  const { theme, emphasis, size, shape, actionType } = props;
  const buttonTheme = getButtonTheme(theme);
  const borderMargin = emphasis === "subtle" ? math(`${focusedBorderMargin} - ${borderWidth}`) : focusedBorderMargin;
  return {
    borderRadius: shape === "circle" ? buttonSizeVariants[size].circleRadius : "4px",
    borderColor: buttonTheme.focus[actionType].borderColor,
    top: borderMargin,
    bottom: borderMargin,
    right: borderMargin,
    left: borderMargin
  };
}
function applyFocusBorderWhenNotFocused(props) {
  const { borderMargin = "0", size, shape } = props;
  return {
    borderRadius: shape === "circle" ? buttonSizeVariants[size].circleRadius : "2px",
    border: `1px solid transparent`,
    top: borderMargin,
    bottom: borderMargin,
    right: borderMargin,
    left: borderMargin
  };
}
function applyIconStyles(props) {
  const { size } = props;
  return {
    fontSize: buttonSizeVariants[size].iconFontSize
  };
}
function applyButtonFocus(props) {
  const { theme, emphasis, actionType, status } = props;
  const buttonTheme = getButtonTheme(theme);
  const notSubtleStyles = {};
  const subtleStyles = {
    borderColor: "transparent"
  };
  const emphasisStyles = emphasis === "subtle" ? subtleStyles : notSubtleStyles;
  return __spreadValues({
    backgroundColor: status !== "none" ? "" : buttonTheme.focus[actionType].backgroundColor,
    color: status !== "none" ? "" : buttonTheme.focus[actionType].textColor,
    outline: 0,
    boxShadow: "none"
  }, emphasisStyles);
}

// components/button/common/src/button/util.ts
import { cx } from "@gs-ux-uitoolkit-common/style";
var globalClassName = "gs-button";
var getRootClassNames = (props) => {
  const { cssClasses, overrideClasses, className } = props;
  return cx(cssClasses.root, className, overrideClasses && overrideClasses.root, globalClassName);
};
var getButtonClassNames = (props) => {
  const { cssClasses, overrideClasses } = props;
  return cx(
    cssClasses.button,
    overrideClasses && overrideClasses.button,
    `${globalClassName}__button`
  );
};

// components/button/common/src/button-select/button-select-props.ts
var defaultButtonSelectProps = {
  size: "md",
  disabled: false
};
var buttonSelectTypes = ["single", "multi"];
var buttonSelectSizes = buttonSizes;

// components/button/common/src/button-select/button-select-style-sheet.ts
import { StyleSheet as StyleSheet2 } from "@gs-ux-uitoolkit-common/style";

// components/button/common/src/button-select/button-select-theme.ts
function getButtonSelectTheme(theme) {
  const selectedShades = theme.getSurfaceInteractionShades("primary", "bold");
  const unselectedShades = theme.getSurfaceInteractionShades("tertiary");
  return {
    selected: {
      backgroundColor: selectedShades.background,
      backgroundHoverColor: selectedShades.hover,
      backgroundActiveColor: selectedShades.active,
      textColor: selectedShades.text
    },
    unselected: {
      backgroundColor: unselectedShades.background,
      backgroundHoverColor: unselectedShades.hover,
      backgroundActiveColor: unselectedShades.active,
      textColor: unselectedShades.text
    }
  };
}

// components/button/common/src/button-select/button-select-style-sheet.ts
var buttonSelectStyleSheet = new StyleSheet2(
  "button-select",
  ({ theme, size = defaultButtonSelectProps.size }) => {
    const buttonSelectTheme = getButtonSelectTheme(theme);
    return {
      root: {
        display: "inline-block",
        [`[data-cy="gs-uitk-button-select__input"]:disabled + [data-cy="gs-uitk-button-select__target"], 
                    [data-cy="gs-uitk-button-select__input"]:disabled + [data-cy="gs-uitk-button-select__target"]:active`]: {
          cursor: "default",
          opacity: theme.state.disabledOpacity
        },
        [`[data-cy="gs-uitk-button-select__input"]:checked + [data-cy="gs-uitk-button-select__target"]`]: {
          color: buttonSelectTheme.selected.textColor,
          background: buttonSelectTheme.selected.backgroundColor,
          "&:hover": {
            background: buttonSelectTheme.selected.backgroundHoverColor
          },
          "&:active": {
            background: buttonSelectTheme.selected.backgroundActiveColor
          }
        },
        [`[data-cy="gs-uitk-button-select__input"]:checked:disabled + [data-cy="gs-uitk-button-select__target"], 
                    [data-cy="gs-uitk-button-select__input"]:checked:disabled + [data-cy="gs-uitk-button-select__target"]:active`]: {
          cursor: "default",
          opacity: theme.state.disabledOpacity
        }
      },
      label: __spreadProps(__spreadValues({}, applyButtonBase2()), {
        padding: "0",
        margin: "0",
        border: "0",
        color: "transparent",
        background: "transparent"
      }),
      input: {
        width: "100%",
        height: "100%",
        position: "absolute",
        opacity: "0",
        pointerEvents: "none",
        margin: 0
      },
      target: __spreadProps(__spreadValues({}, applyButtonBase2()), {
        color: buttonSelectTheme.unselected.textColor,
        background: buttonSelectTheme.unselected.backgroundColor,
        textAlign: "center",
        "&:hover": {
          background: buttonSelectTheme.unselected.backgroundHoverColor
        },
        "&:active": {
          background: buttonSelectTheme.unselected.backgroundActiveColor
        }
      }),
      focusBorder: __spreadValues({}, applyButtonFocusBorder({ theme })),
      size: {
        '[data-cy="gs-uitk-button-select__target"]': __spreadValues(__spreadProps(__spreadValues({}, applyButtonTypography({ size, theme })), {
          height: buttonSizeVariants[size].buttonHeight,
          padding: `${buttonSizeVariants[size].buttonPaddingY} 0px`,
          border: "0px"
        }), applyButtonIconAndTextPositions({ size }))
      }
    };
  }
);
function applyButtonBase2() {
  return __spreadProps(__spreadValues({}, applyButtonCommonBase()), {
    borderRadius: "2px"
  });
}
function applyButtonFocusBorder(props) {
  const { theme } = props;
  const borderMargin = "0";
  const focusBorderNotFocused = {
    borderRadius: "2px",
    border: `1px solid transparent`,
    top: borderMargin,
    bottom: borderMargin,
    right: borderMargin,
    left: borderMargin
  };
  const focusBorderFocused = {
    borderRadius: "4px",
    zIndex: 100,
    borderColor: theme.colorScheme.primary,
    top: focusedBorderMargin,
    bottom: focusedBorderMargin,
    right: focusedBorderMargin,
    left: focusedBorderMargin
  };
  return {
    position: "relative",
    // to position the ::before focus element
    // add the focus border
    "&:before": __spreadValues({
      content: '""',
      position: "absolute",
      display: "block",
      transition: "border-color 0.15s ease-in-out, border-radius 0.15s ease-in-out, top 0.15s ease-in-out, right 0.15s ease-in-out, bottom 0.15s ease-in-out, left 0.15s ease-in-out"
    }, focusBorderNotFocused),
    "&:not(:disabled):active": {
      "&:focus, &:focus-within": {
        "&:before": __spreadValues({}, focusBorderNotFocused)
      }
    },
    "&:focus, &:focus-within": {
      outline: 0,
      boxShadow: "none",
      // instead we use a ::before element to indicate focus
      "&:before": __spreadValues({}, focusBorderFocused)
    }
  };
}

// components/button/common/src/button-select/util.ts
import { cx as cx2 } from "@gs-ux-uitoolkit-common/style";
var globalClassName2 = "gs-button-select";
var getButtonSelectRootClassNames = (props) => {
  const { cssClasses, overrideClasses, className } = props;
  return cx2(
    cssClasses.root,
    cssClasses.size,
    className,
    overrideClasses && overrideClasses.root,
    globalClassName2
  );
};
var getButtonSelectLabelClassNames = (props) => {
  const { cssClasses, overrideClasses } = props;
  return cx2(
    cssClasses.label,
    cssClasses.focusBorder,
    overrideClasses && overrideClasses.label,
    `${globalClassName2}__label`
  );
};
var getButtonSelectInputClassNames = (props) => {
  const { cssClasses, overrideClasses } = props;
  return cx2(
    cssClasses.input,
    overrideClasses && overrideClasses.input,
    `${globalClassName2}__input`
  );
};
var getButtonSelectTargetClassNames = (props) => {
  const { cssClasses, overrideClasses } = props;
  return cx2(
    cssClasses.target,
    overrideClasses && overrideClasses.target,
    `${globalClassName2}__target`
  );
};

// components/button/common/src/button-group/button-group-props.ts
var defaultButtonGroupProps = {
  orientation: "horizontal"
};
var buttonGroupOrientations = ["horizontal", "vertical"];

// components/button/common/src/button-group/button-group-context.ts
var defaultButtonGroupContext = {};

// components/button/common/src/button-group/button-group-style-sheet.ts
import { StyleSheet as StyleSheet3 } from "@gs-ux-uitoolkit-common/style";
var buttonGroupStyleSheet = new StyleSheet3(
  "button-group",
  (_props) => {
    return {
      root: {
        position: "relative",
        display: "inline-flex",
        verticalAlign: "middle"
      },
      ["orientation-horizontal"]: {
        // buttons except first
        [`> [data-gs-uitk-component="button"]:not(:first-child),
                    > [data-gs-uitk-component="button-select"]:not(:first-child)`]: {
          marginLeft: "1px",
          [`[data-cy="gs-uitk-button__button"],
                            [data-cy="gs-uitk-button-select__label"],
                            [data-cy="gs-uitk-button-select__target"]`]: {
            borderTopLeftRadius: "0",
            borderBottomLeftRadius: "0",
            "&:focus, &:focus-within": {
              "&:before": {
                borderTopLeftRadius: "0",
                borderBottomLeftRadius: "0"
              }
            }
          },
          [`&[data-emphasis='subtle']`]: {
            marginLeft: "-1px",
            [`[data-cy="gs-uitk-button__button"]`]: {
              "&:focus, &:focus-within": {
                "&:before": {
                  left: "-1px"
                }
              }
            }
          }
        },
        // subtle styles dont apply when next to a non-subtle button.
        // styles must be set here instead of nested in the "buttons except first" rule
        // so that it also applies to subtle buttons in the 2nd position
        [`> [data-gs-uitk-component="button"]:not([data-emphasis='subtle'])
                    + [data-gs-uitk-component="button"][data-emphasis='subtle']`]: {
          marginLeft: "1px",
          [`[data-cy="gs-uitk-button__button"]`]: {
            "&:focus, &:focus-within": {
              "&:before": {
                left: focusedBorderMargin
              }
            }
          }
        },
        // buttons except last
        [`> [data-gs-uitk-component="button"]:not(:last-child),
                    > [data-gs-uitk-component="button-select"]:not(:last-child)`]: {
          [`[data-cy="gs-uitk-button__button"],
                            [data-cy="gs-uitk-button-select__label"],
                            [data-cy="gs-uitk-button-select__target"]`]: {
            borderTopRightRadius: "0",
            borderBottomRightRadius: "0",
            "&:focus, &:focus-within": {
              "&:before": {
                borderTopRightRadius: "0",
                borderBottomRightRadius: "0"
              }
            },
            [`&[data-emphasis='subtle']`]: {
              "&:focus, &:focus-within": {
                "&:before": {
                  right: "-1px"
                }
              }
            }
          },
          [`&:not([data-emphasis='subtle'])
                            + [data-gs-uitk-component="button"][data-emphasis='subtle']`]: {
            [`> [data-cy="gs-uitk-button__button"]`]: {
              "&:focus, &:focus-within": {
                "&:before": {
                  right: focusedBorderMargin
                }
              }
            }
          }
        }
      },
      ["orientation-vertical"]: {
        flexDirection: "column",
        // buttons except first
        [`> [data-gs-uitk-component="button"]:not(:first-child),
                    > [data-gs-uitk-component="button-select"]:not(:first-child)`]: {
          marginTop: "1px",
          [`[data-cy="gs-uitk-button__button"],
                            [data-cy="gs-uitk-button-select__label"],
                            [data-cy="gs-uitk-button-select__target"]`]: {
            borderTopLeftRadius: "0",
            borderTopRightRadius: "0",
            "&:focus, &:focus-within": {
              "&:before": {
                borderTopLeftRadius: "0",
                borderTopRightRadius: "0"
              }
            },
            [`&[data-emphasis='subtle']`]: {
              marginTop: "-1px",
              [`[data-cy="gs-uitk-button__button"]`]: {
                "&:focus, &:focus-within": {
                  "&:before": {
                    top: "-1px"
                  }
                }
              }
            }
          }
        },
        // subtle styles dont apply when next to a non-subtle button.
        // styles must be set here instead of nested in the "buttons except first" rule
        // so that it also applies to subtle buttons in the 2nd position
        [`> [data-gs-uitk-component="button"]:not([data-emphasis='subtle'])
                    + [data-gs-uitk-component="button"][data-emphasis='subtle']`]: {
          marginTop: "1px",
          [`[data-cy="gs-uitk-button__button"]`]: {
            "&:focus, &:focus-within": {
              "&:before": {
                top: focusedBorderMargin
              }
            }
          }
        },
        // buttons except last
        [`> [data-gs-uitk-component="button"]:not(:last-child),
                    > [data-gs-uitk-component="button-select"]:not(:last-child)`]: {
          [`[data-cy="gs-uitk-button__button"],
                            [data-cy="gs-uitk-button-select__label"],
                            [data-cy="gs-uitk-button-select__target"]`]: {
            borderBottomLeftRadius: "0",
            borderBottomRightRadius: "0",
            "&:focus, &:focus-within": {
              "&:before": {
                borderBottomLeftRadius: "0"
              },
              [`&[data-emphasis='subtle']`]: {
                "&:focus, &:focus-within": {
                  "&:before": {
                    bottom: "-1px"
                  }
                }
              }
            }
          },
          [`&:not([data-emphasis='subtle'])
                            + [data-gs-uitk-component="button"][data-emphasis='subtle']`]: {
            [`> [data-cy="gs-uitk-button__button"]`]: {
              "&:focus, &:focus-within": {
                "&:before": {
                  bottom: focusedBorderMargin
                }
              }
            }
          }
        }
      }
    };
  }
);

// components/button/common/src/button-group/util.ts
import { cx as cx3 } from "@gs-ux-uitoolkit-common/style";
var globalClassName3 = "gs-button-group";
var getButtonGroupClassNames = (props) => {
  const { orientation, cssClasses, overrideClasses, className } = props;
  const rootOverride = overrideClasses && overrideClasses.root;
  return cx3(
    cssClasses.root,
    {
      [cssClasses["orientation-horizontal"]]: orientation === "horizontal",
      [cssClasses["orientation-vertical"]]: orientation === "vertical"
    },
    className,
    rootOverride,
    globalClassName3
  );
};
export {
  buttonActionTypes,
  buttonEmphasises,
  buttonGroupOrientations,
  buttonGroupStyleSheet,
  buttonSelectSizes,
  buttonSelectStyleSheet,
  buttonSelectTypes,
  buttonShapes,
  buttonSizeVariants,
  buttonSizes,
  buttonStatuses,
  buttonStyleSheet,
  buttonTypes,
  defaultButtonGroupContext,
  defaultButtonGroupProps,
  defaultButtonProps,
  defaultButtonSelectProps,
  getButtonClassNames,
  getButtonGroupClassNames,
  getButtonSelectInputClassNames,
  getButtonSelectLabelClassNames,
  getButtonSelectRootClassNames,
  getButtonSelectTargetClassNames,
  getRootClassNames
};
//# sourceMappingURL=index.mjs.map
